import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReferralLetter } from "@/lib/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { NewReferralTo } from "./NewReferralTo";
import { NewReferralReply } from "./NewReferralReply";
import type { LetterType } from "@emr/shared/constants";

/**
 * Dispatcher for the legacy `/referrals/new` and `/referrals/:id` routes.
 *
 * - `/referrals/new` (no `id`): renders the matching dedicated editor directly
 *   based on the `letterType` query param (`Incoming` → `NewReferralReply`,
 *   anything else → `NewReferralTo`). No redirect — no skeleton flash.
 * - `/referrals/:id` (editing an existing letter): fetches the letter once to
 *   learn its `letterType`, then redirects to the canonical route
 *   (`/referrals/to/:id` or `/referrals/reply/:id`) where the right editor
 *   renders directly. The canonical routes bypass this dispatcher.
 */
export function NewReferralLetter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const letterTypeParam = (searchParams.get("letterType") as LetterType | null) ?? null;

  const skipFetch = !id;
  const { data: existing, isLoading } = useReferralLetter(skipFetch ? undefined : id);

  const decided = useRef(false);

  useEffect(() => {
    if (decided.current || skipFetch) return;
    if (isLoading) return;
    const lt = (existing?.letter as any)?.letterType;
    if (lt === "Incoming") {
      navigate(`/referrals/reply/${id}`, { replace: true });
      decided.current = true;
    } else if (lt === "Outgoing" || lt) {
      navigate(`/referrals/to/${id}`, { replace: true });
      decided.current = true;
    }
  }, [skipFetch, isLoading, existing, id, navigate]);

  // New letter — render the right editor directly (no redirect).
  if (skipFetch) {
    return letterTypeParam === "Incoming" ? <NewReferralReply /> : <NewReferralTo />;
  }

  // Editing existing letter — show a skeleton while we fetch + redirect.
  // eslint-disable-next-line react-hooks/refs -- deliberate: `decided` guards against a redirect loop after navigate(); reading it during render only picks between two identical skeletons
  if (isLoading || !decided.current) {
    return <div className="p-6"><Skeleton className="h-96" /></div>;
  }
  return <div className="p-6"><Skeleton className="h-96" /></div>;
}
